import { createSelector } from 'reselect';
import { ForecastAmountSettingOptions } from '../constants/PortalConfigConstants';
import { MAX_FORECASTS } from 'ui-deal-management-components-lib/constants/ForecastType';
import { DEAL_STAGE } from '../constants/DealGroupingType';
import { FILTERS } from '../constants/FiltersConstants';
import { getDealGroupingTypeFilter, getForecastTypeFilter } from './filtersSelectors';
import { getFilters } from './filtersSelectors';
import { getIsUngatedToForecastingMultipleOutcomes } from '../selectors/authSelectors';
import { FAILED } from '../constants/RequestStatusTypes';
import { getPropertyDisplayName } from 'ui-deal-management-components-lib/utils/dealPropertyUtils';
import { getProperties } from './properties';
import { DEFAULT_FORECAST_TYPE_AMOUNT_TYPE, DEFAULT_FORECAST_TYPE_DATE_TYPE, HUBSPOT_DEFINED_FORECAST_TYPE_ID } from '../constants/ForecastTypeConstants';
const getForecast = state => state.forecast;
export const getPipelineCadence = state => {
  return getForecast(state).getIn(['pipelineCadence', 'cadence']);
};
export const getPipelines = state => {
  return getForecast(state).get('pipelines');
};
export const getPortalConfig = state => {
  return getForecast(state).get('portalConfig');
};
export const getIsWeightedAmountPortalConfig = createSelector([getPortalConfig], portalConfig => {
  return portalConfig === ForecastAmountSettingOptions.WEIGHTED_AMOUNT;
});
export const getIsWeightedOrTotalPortalConfig = createSelector([getPortalConfig], portalConfig => {
  return portalConfig === ForecastAmountSettingOptions.WEIGHTED_AMOUNT || portalConfig === ForecastAmountSettingOptions.TOTAL_AMOUNT;
});
export const getIsDealStageView = createSelector([getDealGroupingTypeFilter], dealGroupingType => {
  return dealGroupingType === DEAL_STAGE;
});
export const getPortalWideCadence = state => getForecast(state).getIn(['portalWideCadence', 'cadence']);
export const getIsAllPipelines = createSelector([getPipelines, getFilters, getIsDealStageView], (pipelines, filters, isDealStageView) => {
  if (isDealStageView) {
    return false;
  }
  const numTotalPipelines = pipelines.size;
  const numSelectedPipelines = filters.get(FILTERS.PIPELINE).size;
  return numTotalPipelines === numSelectedPipelines;
});
export const getHasForecastTypesFetchError = state => {
  return getForecast(state).get('forecastTypesStatus') === FAILED;
};
export const getForecastTypes = state => {
  return getForecast(state).get('forecastTypes');
};
export const getHasMaxForecastTypes = createSelector([getForecastTypes], forecastTypes => {
  return (forecastTypes === null || forecastTypes === void 0 ? void 0 : forecastTypes.length) >= MAX_FORECASTS;
});
export const getSelectedForecastType = createSelector([getForecastTypeFilter, getForecastTypes], (forecastTypeId, forecastTypes) => {
  return forecastTypes === null || forecastTypes === void 0 ? void 0 : forecastTypes.find(forecastType => forecastType.forecastTypeId === forecastTypeId);
});
export const getSelectedForecastTypeIsDefault = createSelector([getSelectedForecastType], forecastType => {
  return forecastType === null || forecastType === void 0 ? void 0 : forecastType.defaultType;
});
export const getSelectedForecastTypeIdOrGatedDefault = createSelector([getIsUngatedToForecastingMultipleOutcomes, getSelectedForecastType], (isUngatedToForecastingMultipleOutcomes, forecastType) => {
  return isUngatedToForecastingMultipleOutcomes && forecastType ? forecastType.forecastTypeId : HUBSPOT_DEFINED_FORECAST_TYPE_ID;
});
export const getSelectedForecastTypeProperties = createSelector([getSelectedForecastType, getProperties], (forecastType, properties) => {
  return {
    amountProperty: getPropertyDisplayName(properties, forecastType === null || forecastType === void 0 ? void 0 : forecastType.amountProperty),
    dateProperty: getPropertyDisplayName(properties, forecastType === null || forecastType === void 0 ? void 0 : forecastType.dateProperty)
  };
});
export const getSelectedForecastTypePropertiesOrGatedDefault = createSelector([getIsUngatedToForecastingMultipleOutcomes, getSelectedForecastType], (isUngatedToForecastingMultipleOutcomes, selectedForecastType) => {
  return isUngatedToForecastingMultipleOutcomes ? {
    amountProperty: selectedForecastType === null || selectedForecastType === void 0 ? void 0 : selectedForecastType.amountProperty,
    dateProperty: selectedForecastType === null || selectedForecastType === void 0 ? void 0 : selectedForecastType.dateProperty
  } : {
    amountProperty: DEFAULT_FORECAST_TYPE_AMOUNT_TYPE,
    dateProperty: DEFAULT_FORECAST_TYPE_DATE_TYPE
  };
});
export const getHubSpotDefinedForecastType = createSelector([getForecastTypes], forecastTypes => {
  return forecastTypes === null || forecastTypes === void 0 ? void 0 : forecastTypes.find(forecastType => forecastType.defaultType);
});
export const getUserHasAccessToNoForecastTypes = createSelector([getIsUngatedToForecastingMultipleOutcomes, getForecastTypes], (isUngatedToForecastingMultipleOutcomes, forecastTypes) => {
  return isUngatedToForecastingMultipleOutcomes && (forecastTypes === null || forecastTypes === void 0 ? void 0 : forecastTypes.every(forecastType => forecastType.amountOrDatePropertyRestrictedForUser));
});
export const getUserHasAccessToMoreThanDefaultForecastType = createSelector([getIsUngatedToForecastingMultipleOutcomes, getForecastTypes], (isUngatedToForecastingMultipleOutcomes, forecastTypes) => {
  return isUngatedToForecastingMultipleOutcomes && (forecastTypes === null || forecastTypes === void 0 ? void 0 : forecastTypes.filter(forecastType => !forecastType.amountOrDatePropertyRestrictedForUser).length) > 1;
});
export const getRepDealColumnsStatus = createSelector([getForecast], forecast => {
  return forecast.get('repDealColumnsStatus');
});
export const getRepDealColumnsError = createSelector([getRepDealColumnsStatus], status => {
  return status === FAILED;
});
export const getRepDealColumns = state => {
  return getForecast(state).get('repDealColumns');
};
export const getRepDealColumnsForSelectedForecastType = createSelector([getSelectedForecastTypeIdOrGatedDefault, getRepDealColumns], (selectedForecastTypeIdOrGatedDefault, repDealColumns) => {
  var _repDealColumns$find$, _repDealColumns$find;
  return (_repDealColumns$find$ = repDealColumns === null || repDealColumns === void 0 || (_repDealColumns$find = repDealColumns.find(repDealColumn => repDealColumn.forecastTypeId === selectedForecastTypeIdOrGatedDefault)) === null || _repDealColumns$find === void 0 ? void 0 : _repDealColumns$find.dealColumns) !== null && _repDealColumns$find$ !== void 0 ? _repDealColumns$find$ : [];
});